import { Div, Button, router, state, waitFor, IRouteEvent } from '@codesuma/baseline'
import { Slider, ISliderFill } from '../../components/slider'
import { SliderArrows } from '../../components/slider-arrows'
import { BackIcon } from '../../components/back-icon'
import { YearSlide } from './year-slide'
import calendar, { IDayCell } from '../../services/calendar'
import settings from '../../services/settings'
import { viewShell } from '../../helpers/view-shell'
import style from './index.module.css'

// The year view (route /year?y=<year>): twelve month grids on one page, with
// the days' own colours on them, the whole year at once. A horizontal swipe
// moves a year at a time, the way the home page moves a month and the day page
// a day.
//
// The year in the query is in the active calendar system — settings already
// knows which that is, so it needs no second parameter.
//
// A Div and not a baseline Page: this page waits ABOVE the calendar and settles
// down into it, which is the gesture that opens it — the month is pulled down
// and the year is what was above it. Baseline's Page only ever rises from
// below, so the movement is owned here (see .base and .shown). The waits below
// are baseline's own, so the year arrives on the beat the menu does.
export const YearPage = () => {
    const base = Div()
    base.addClass(style.base)
    base.attr('aria-hidden', 'true')

    // The year at slider index 0. Every slide renders anchor + its own index,
    // the same way the day page anchors on the tapped date.
    let anchor = calendar.today().jy
    let hasBack = false // false on deep links, where history has no entry to go back to

    const slider = Slider()
    slider.on('fill', ({ slide, index }: ISliderFill) => {
        slide.empty()
        const year = YearSlide(anchor + index)
        year.on('day-open', (c: IDayCell) => router.goto(`/day?d=${c.gy}-${c.gm}-${c.gd}`))
        // A month card hands back the home slider's index for that month, so
        // the calendar comes up sitting on it.
        year.on('month-open', (m: number) => router.goto(`/?m=${m}`))
        year.on('century-open', (y: number) => router.goto(`/century?y=${y}`))
        slide.append(year)
    })
    base.append(slider)

    // The year steppers, the same pair the calendar carries.
    const arrows = SliderArrows(slider, 'سال')
    base.append(arrows)

    // Gregorian mode reads LTR, so the slider advances that way too — and the
    // arrows' labels swap sides with it. The page turns with them, which is
    // what carries the dismiss to the corner the year now starts from, the way
    // the day page does it.
    const syncDirection = () => {
        const ltr = settings.get().system === 'gregorian'
        slider.direction(ltr ? 'ltr' : 'rtl')
        base.attr('dir', ltr ? 'ltr' : 'rtl')
        arrows.sync()
    }

    const dismiss = Button()
    dismiss.addClass(style.dismiss)
    dismiss.attr('aria-label', 'برگشت')
    dismiss.attr('data-ripple', '')
    dismiss.append(BackIcon())
    dismiss.on('click', () => hasBack ? router.back() : router.goto('/'))
    base.append(dismiss)

    // The year this page is currently anchored on; a new one rewinds the slider.
    let anchored = 0

    base.on('enter', async ({ query, from }: IRouteEvent) => {
        const y = Number(query?.y)
        const today = calendar.today()
        anchor = Number.isFinite(y) && y > 0
            ? y
            : settings.get().system === 'gregorian' ? today.gy : today.jy

        syncDirection()
        if (anchor !== anchored) {
            anchored = anchor
            if (slider.getCursor() !== 0) slider.reset()
            else slider.fill()
        } else {
            slider.fill()
        }

        hasBack = !!from
        viewShell(true)

        // Which side to arrive from. Coming back down from the years view the
        // page rises from below, where it went; from anywhere else it settles
        // down from above. Positioned without animation first — the movement is
        // the class change after the wait, not getting into place for it.
        base.style({ transition: 'none' })
        base.toggleClass(style.exitingDown, from === '/century')
        void base.el.offsetHeight
        base.style({ transition: '' })
        // The page below is on its way; arrive as it finishes, not over the top.
        await waitFor(200)
        base.removeClass(style.exitingDown)
        base.addClass(style.shown)
        base.attr('aria-hidden', 'false')
    })

    // Out of the way: down for the years view, which waits above, and back up
    // for everything else, which is below. The router awaits this, so the next
    // page starts moving only once this one has gone. The shell keeps the
    // surface only when the years view is what it is handing over to.
    base.on('exit', async ({ to }: IRouteEvent) => {
        base.removeClass(style.shown)
        base.toggleClass(style.exitingDown, to === '/century')
        viewShell(to === '/century')
        await waitFor(160)
        base.attr('aria-hidden', 'true')
    })

    // A moon-sighting correction moves the hijri occasions the holidays are
    // marked from, and a saved occasion adds one — redraw where the user stands.
    // Not 'settings': the calendar system can only be switched on the home page,
    // and the anchor year is written in whichever system was chosen there. The
    // query carries the right one on the way back in.
    // A day that turns moves today's disc from one card to the next — and at
    // Nowruz, off the twelve altogether. The anchor year is not touched: this
    // page is anchored by its query, so a redraw is all it needs.
    const refill = () => slider.fill()
    state.on('hijri', refill)
    state.on('custom-occasions', refill)
    state.on('today', refill)
    base.on('unmounted', () => {
        state.off('hijri', refill)
        state.off('custom-occasions', refill)
        state.off('today', refill)
    })

    return base
}
